import { useMemo, useState } from 'react';

import { attachLogicRules, deleteRuleAndRevealedSubtree, DropdownFormElement, FormElement, LogicRule, reconcileShowFieldRule } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery } from '@tanstack/react-query';

import CloneIcon from '@/components/icons/clone';
import DeleteIcon from '@/components/icons/delete';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import { Select } from '@/components/ui/select';
import { getListOptionsQueryOptions } from '@/queries/lists';
import { useIsAWA } from '@/utils/awa';

import { LogicRuleConfigLabels } from '../config-field-labels';

type OperatorOption = LogicRule['operator'];
type SelectOption = { label: string; value: string };

const OPERATOR_OPTIONS: { label: string; value: OperatorOption }[] = [
  { label: LogicRuleConfigLabels.operatorIs, value: 'is' },
  { label: LogicRuleConfigLabels.operatorIsNot, value: 'is not' },
];

export function DropdownLogicRuleBuilder({
  element,
  ruleId,
  allElements,
  onElementsChange,
  onClone,
  onClose,
  invalidValues = false,
}: {
  element: DropdownFormElement;
  // Owned by the parent so the same id keys both this rule row and the reveal-box drop target.
  ruleId: string;
  allElements: FormElement[];
  onElementsChange: (next: FormElement[]) => void;
  // Clone this rule — deep-copies the revealed subtree into a new value-less rule. Owned by the parent
  // because it inserts elements into the array (the row component only edits one rule's values/operator).
  onClone: () => void;
  onClose: () => void;
  // On a save attempt, flag the values control red when this rule is invalid (incomplete, conflicting, or
  // referencing a deleted value) — the parent owns that per-rule determination.
  invalidValues?: boolean;
}) {
  const rule = element.logicRules?.find((candidate) => candidate.id === ruleId);

  const operator: OperatorOption = rule?.operator ?? 'is';
  const values = rule?.values ?? [];
  const revealedIds = allElements.filter((el) => el.dependsOn?.logicRuleId === ruleId).map((el) => el.id);

  const valueOptions = useDropdownValueOptions(element);

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Deleting the rule cascade-destroys the fields it reveals (and their nested descendants) and closes the row,
  // matching the card-level delete. Gated behind a confirm only when it would destroy fields — an empty rule
  // deletes straight through. The count is the array-length delta so nested descendants are included.
  const cascadeDeleteCount = allElements.length - deleteRuleAndRevealedSubtree(allElements, element.id, ruleId).length;

  function deleteRule() {
    onElementsChange(deleteRuleAndRevealedSubtree(allElements, element.id, ruleId));
    onClose();
  }

  function apply(next: { operator?: OperatorOption; values?: string[]; dependentIds?: string[] }) {
    const nextOperator = next.operator ?? operator;
    const nextValues = next.values ?? values;
    const nextDependentIds = next.dependentIds ?? revealedIds;

    // reconcileShowFieldRule drops a rule with neither values nor dependents, so picking an operator before a
    // match value is chosen would reconcile the rule straight back out and the control would snap to 'is'.
    // An operator change is the user building the rule — upsert it so the choice sticks; save-time validation
    // still blocks the incomplete rule. Clearing the rule (the delete button) passes no operator and falls
    // through to reconcile, which correctly removes it.
    const wouldDrop = nextValues.length === 0 && nextDependentIds.length === 0;
    if (next.operator !== undefined && wouldDrop) {
      onElementsChange(
        attachLogicRules(allElements, [{ triggerId: element.id, ruleId, operator: nextOperator, values: nextValues, dependentIds: nextDependentIds }]),
      );
      return;
    }

    onElementsChange(
      reconcileShowFieldRule(allElements, {
        triggerId: element.id,
        ruleId,
        operator: nextOperator,
        values: nextValues,
        dependentIds: nextDependentIds,
      }),
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 py-2.5">
        <span className="text-default-text shrink-0 text-sm">{LogicRuleConfigLabels.ifValue}</span>

        <div className="w-25 shrink-0">
          <Select<{ label: string; value: OperatorOption }>
            aria-label={LogicRuleConfigLabels.operator}
            classNamePrefix={`${element.id}-operator`}
            isSearchable={false}
            options={OPERATOR_OPTIONS}
            value={OPERATOR_OPTIONS.find((option) => option.value === operator)}
            onChange={(selected) => selected && apply({ operator: selected.value })}
          />
        </div>

        <div className="min-w-[180px] flex-1">
          <MultiSelect<SelectOption>
            aria-label={LogicRuleConfigLabels.matchValues}
            classNamePrefix={`${element.id}-values`}
            placeholder={LogicRuleConfigLabels.valuesPlaceholder}
            error={invalidValues}
            options={valueOptions}
            value={valueOptions.filter((option) => values.includes(option.value))}
            onChange={(selected) => apply({ values: selected.map((option) => option.value) })}
          />
        </div>

        <span className="text-default-text shrink-0 text-sm">{LogicRuleConfigLabels.then}</span>

        {/* Decorative: reveal targets are chosen by dragging fields into the box below, not from this control. */}
        <div className="w-50 shrink-0">
          <Select<SelectOption>
            aria-label={LogicRuleConfigLabels.showFields}
            classNamePrefix={`${element.id}-show-fields`}
            isDisabled
            options={[{ label: LogicRuleConfigLabels.showFields, value: 'show-fields' }]}
            value={{ label: LogicRuleConfigLabels.showFields, value: 'show-fields' }}
          />
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={LogicRuleConfigLabels.cloneRule}
            className="bg-grey-1 h-auto w-auto p-1"
            onClick={onClone}>
            <CloneIcon />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={LogicRuleConfigLabels.deleteRule}
            className="bg-grey-1 h-auto w-auto p-1"
            onClick={() => {
              if (cascadeDeleteCount === 0) {
                deleteRule();
                return;
              }
              setConfirmOpen(true);
            }}>
            <DeleteIcon className="text-default-red" />
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this rule and its revealed fields?"
        description={`This also deletes ${cascadeDeleteCount} revealed ${cascadeDeleteCount === 1 ? 'field' : 'fields'} shown by this rule. This can't be undone.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        confirmIcon={<DeleteIcon className="text-white" />}
        onConfirm={deleteRule}
      />
    </>
  );
}

function useDropdownValueOptions(element: DropdownFormElement): SelectOption[] {
  const { isAWA } = useIsAWA();
  const listId = element.config?.source === 'list' ? element.config.listId : '';
  const { data: fetchedOptions } = useQuery({
    ...getListOptionsQueryOptions({ listId, isAWA }),
    enabled: element.config?.source === 'list' && !!listId,
  });

  return useMemo(() => {
    if (element.config?.source === 'static') {
      return element.config.options.map((option) => ({ label: option, value: option }));
    }
    if (element.config?.source === 'list') {
      return (fetchedOptions ?? []).map((option: string) => ({ label: option, value: option }));
    }
    return [];
  }, [element.config, fetchedOptions]);
}
